import { Box, Grid, Tooltip } from "@mui/material";
import {
    ISignalsChartDefinition,
    SignalsObjectType,
} from "../../SignalsModels";
import { StatusTagChip } from "components/UI/StatusTags";
import { getEndpointBehaviorConfig } from "components/Signals/BaseObjects/_DataEndpointConfig";
import { getChartPreviewImage } from "./chartPreviewImage";
import { useStore } from "utils/useStore";
import SignalsCardSelectorStore from "./SignalsCardSelectorStore";
import theme from "Theme/AppTheme";
import QuantityPill from "./QuantityPill";

const PREVIEW_WIDTH = 305;
const PREVIEW_HEIGHT = 144;
// The title bar is always this tall so one- and two-line titles yield equal-height
// cards. Expressed in em so it tracks whatever font size the title inherits.
const TITLE_LINES = 2;
const TITLE_LINE_HEIGHT = 1.5;

const ChartOption = ({
    chart,
    chartLimitReached,
}: {
    chart: ISignalsChartDefinition;
    chartLimitReached: boolean;
}) => {
    const store = useStore(SignalsCardSelectorStore);

    const isTableChart = chart.objectType === SignalsObjectType.Table;
    const isUniqueChart = chart.objectType === SignalsObjectType.Unique;
    const chartCount = isTableChart
        ? store.getTableCount(chart.id)
        : isUniqueChart
        ? store.getUniqueChartCount(chart.id)
        : store.getChartCount(chart.id);
    const isSelected = chartCount > 0;
    const unavailableReason = store.getChartUnavailableReason(chart);
    const disableSelection =
        !isSelected && (chartLimitReached || !!unavailableReason);
    const previewSrc = getChartPreviewImage(chart);
    // The tile has no card store, so it resolves the endpoint's config itself. Both the status tag
    // and the Pendo id come from that one declaration; endpoints declaring no addCard id render
    // without one and go untracked.
    const endpointConfig = chart.dataEndpoint
        ? getEndpointBehaviorConfig({ dataEndpoint: chart.dataEndpoint })
        : undefined;
    const status = endpointConfig?.status;
    const pendoId = endpointConfig?.pendo?.addCard;

    const addSelection = () => {
        if (isTableChart) {
            store.addTableSelection(chart);
            return;
        }
        if (isUniqueChart) {
            store.addUniqueChartSelection(chart);
            return;
        }
        store.addChartSelection(chart);
    };

    const removeSingleSelection = () => {
        if (isTableChart) {
            store.removeTableSelection(chart.id);
            return;
        }
        if (isUniqueChart) {
            store.removeUniqueChartSelection(chart.id);
            return;
        }
        store.removeChartSelection(chart.id);
    };

    const removeAllSelections = () => {
        if (isTableChart) {
            store.removeAllTableSelections(chart.id);
            return;
        }
        if (isUniqueChart) {
            store.removeAllUniqueChartSelections(chart.id);
            return;
        }
        store.removeAllChartSelections(chart.id);
    };

    const handleCardClick = () => {
        if (isSelected) {
            removeAllSelections();
        } else if (!disableSelection) {
            addSelection();
        }
    };

    const chartTile = (
        <Grid
            item
            key={chart.id}
            id={pendoId}
            onClick={handleCardClick}
            sx={{ userSelect: "none" }}
        >
            <Grid
                item
                margin={theme.spacing(1)}
                sx={{
                    width: `${PREVIEW_WIDTH}px`,
                    minWidth: `${PREVIEW_WIDTH}px`,
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: "8px",
                    border: `1px solid ${
                        isSelected
                            ? theme.palette.primary.main
                            : theme.palette.gray[200]
                    }`,
                    "&:hover": {
                        border: `1px solid ${
                            isSelected
                                ? theme.palette.primary.main
                                : theme.palette.gray[500]
                        } !important`,
                        cursor: disableSelection ? "default" : "pointer",
                    },
                    opacity: disableSelection ? 0.6 : 1,
                    backgroundColor: theme.palette.white.main,
                    contain: "layout paint",
                }}
            >
                {status && (
                    <Box
                        sx={{
                            // Top-left because QuantityPill owns top-right once selected.
                            position: "absolute",
                            top: theme.spacing(1),
                            left: theme.spacing(1),
                            zIndex: 3,
                        }}
                    >
                        {/* Spread, not explicit props: the descriptor also carries an
                                optional `permission`, and listing fields by hand silently drops
                                whatever is added to the shape later. */}
                        <StatusTagChip {...status} />
                    </Box>
                )}
                {isSelected && (
                    <QuantityPill
                        count={chartCount}
                        onIncrement={() => {
                            if (!chartLimitReached && !unavailableReason) {
                                addSelection();
                            }
                        }}
                        onDecrement={() => {
                            if (chartCount <= 1) {
                                removeAllSelections();
                                return;
                            }
                            removeSingleSelection();
                        }}
                        disableIncrement={
                            chartLimitReached || !!unavailableReason
                        }
                    />
                )}
                <Box
                    component="img"
                    src={previewSrc}
                    alt={`chart placeholder for ${chart.title}`}
                    draggable={false}
                    width={PREVIEW_WIDTH}
                    height={PREVIEW_HEIGHT}
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        display: "block",
                        width: "100%",
                        height: `${PREVIEW_HEIGHT}px`,
                        objectFit: "cover",
                        backfaceVisibility: "hidden",
                        WebkitTransform: "translateZ(0)",
                    }}
                />
                <Grid
                    item
                    container
                    justifyContent={"center"}
                    alignItems={"center"}
                    style={{
                        backgroundColor: theme.palette.white.main,
                        position: "relative",
                        marginTop: "-15px",
                        zIndex: 2,
                        borderTop: `1px solid ${theme.palette.gray[200]}`,
                        padding: theme.spacing(1),
                        width: "100%",
                        boxSizing: "border-box",
                        height: `calc(${
                            TITLE_LINES * TITLE_LINE_HEIGHT
                        }em + ${theme.spacing(2)})`,
                        textAlign: "center",
                    }}
                >
                    <Box
                        sx={{
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: TITLE_LINES,
                            overflow: "hidden",
                            lineHeight: TITLE_LINE_HEIGHT,
                            width: "100%",
                        }}
                    >
                        {chart.title}
                    </Box>
                </Grid>
            </Grid>
        </Grid>
    );

    return unavailableReason ? (
        <Tooltip title={unavailableReason}>{chartTile}</Tooltip>
    ) : (
        chartTile
    );
};

export default ChartOption;
